import type { Collector } from "./types";
import { isBrowser } from "./types";

export interface StorageInfo {
  idb?: boolean;
  ls?: boolean;
  sw?: boolean;
  swOK?: boolean;
  cacheOK?: boolean;
  opfsSupported?: boolean;
  opfsOK?: boolean;
  quotaMB?: number;
  usageMB?: number;
  persistent?: boolean;
  firefoxPrivateIndicators?: { score: number; signals: string[] };
  bravePrivateIndicators?: { score: number; signals: string[] };
  chromiumPrivateIndicators?: { score: number; signals: string[] };
  timerPrecision?: number;
  // v3.4.0 — modern probes adopted from detectIncognito v1.7.0 (the legacy
  // Chrome quota test was removed upstream because Chrome neutered the signal):
  /** Chrome family: OPFS flush() is a no-op in incognito (memory-backed) vs an
   *  fsync on disk in normal mode. true ⇒ incognito. undefined ⇒ could not run. */
  opfsFlushPrivate?: boolean;
  /** Safari: navigator.storage.getDirectory() throws "unknown transient reason"
   *  in Private mode. true ⇒ private. */
  safariOpfsPrivate?: boolean;
}

/**
 * Enhanced storage + capability probes with browser-specific private mode
 * detection. The collector reads ctx.browser.isBrave to gate the Brave-only
 * quota probe; Firefox detector branches on UA internally.
 */
export const storageCollector: Collector<StorageInfo> = {
  name: "storage",
  version: 1,
  timeoutMs: 5000,
  async collect(ctx) {
    return await storageInfo(!!ctx.browser?.isBrave);
  },
};

async function storageInfo(isBrave: boolean): Promise<StorageInfo> {
  if (!isBrowser) return {};
  const out: StorageInfo = {};

  // IndexedDB
  try {
    const req = indexedDB.open("fp_probe_db");
    await new Promise<void>((resolve) => {
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
    out.idb = !!req.result;
    try {
      req.result?.close();
      indexedDB.deleteDatabase("fp_probe_db");
    } catch {}
  } catch {
    out.idb = false;
  }

  // localStorage
  try {
    localStorage.setItem("__fp_t", "1");
    localStorage.removeItem("__fp_t");
    out.ls = true;
  } catch {
    out.ls = false;
  }

  // Service worker (ephemeral)
  out.sw = "serviceWorker" in navigator;
  out.swOK = false;
  try {
    if (out.sw && location.protocol === "https:") {
      const code =
        'self.addEventListener("install",()=>self.skipWaiting());self.addEventListener("activate",()=>self.clients.claim());';
      const blob = new Blob([code], { type: "text/javascript" });
      const url = URL.createObjectURL(blob);
      const reg = await navigator.serviceWorker.register(url, { type: "module", scope: "/" });
      out.swOK = !!reg;
      await reg.unregister();
      URL.revokeObjectURL(url);
    }
  } catch {
    out.swOK = false;
  }

  // CacheStorage
  out.cacheOK = false;
  try {
    if ("caches" in window) {
      const c = await caches.open("fp_cache_probe");
      await c.put(new Request("/__fp__/probe", { method: "GET" }), new Response("ok"));
      const keys = await caches.keys();
      out.cacheOK = keys.includes("fp_cache_probe");
      await caches.delete("fp_cache_probe");
    }
  } catch {
    out.cacheOK = false;
  }

  // OPFS
  out.opfsSupported = !!(navigator.storage && (navigator.storage as any).getDirectory);
  out.opfsOK = false;
  try {
    if (out.opfsSupported) {
      const root: any = await (navigator.storage as any).getDirectory();
      const f = await root.getFileHandle("fp_probe.bin", { create: true });
      const w = await f.createWritable();
      await w.write(new Uint8Array(16));
      await w.close();
      await root.removeEntry("fp_probe.bin");
      out.opfsOK = true;
    }
  } catch (e) {
    out.opfsOK = false;
    // Safari Private-mode tell (detectIncognito v1.7.0): getDirectory() rejects
    // with a message containing "unknown transient reason". Any other failure
    // (e.g. embedded/third-party frame) is NOT a private-mode signal.
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes("unknown transient reason")) out.safariOpfsPrivate = true;
  }

  // Storage estimate + persistence
  try {
    const anyNav: any = navigator;
    if (anyNav.storage?.estimate) {
      const { quota, usage } = await anyNav.storage.estimate();
      if (typeof quota === "number") out.quotaMB = Math.max(0, Math.round(quota / 1024 / 1024));
      if (typeof usage === "number") out.usageMB = Math.max(0, Math.round(usage / 1024 / 1024));
      if (anyNav.storage.persisted) {
        const p = await anyNav.storage.persisted();
        if (typeof p === "boolean") out.persistent = p;
      }
    }
  } catch {
    /* ignore */
  }

  out.firefoxPrivateIndicators = detectFirefoxPrivateMode(out);
  out.bravePrivateIndicators = isBrave ? await detectChromiumPrivateModeViaQuota() : { score: 0, signals: [] };
  if (shouldRunChromiumQuotaProbeForChromeFamily()) {
    out.chromiumPrivateIndicators = await detectChromiumPrivateModeViaQuota();
  }
  // OPFS flush-timing works on ANY desktop Blink browser (Chrome/Edge/Opera/Brave): incognito OPFS
  // is memory-backed so flush() is ~free. Brave is excluded from the quota gate above (it has its own
  // bravePrivateIndicators), so call the OPFS probe explicitly for it too. The probe self-guards
  // (returns undefined without OPFS + Worker, e.g. on WebKit), so over-calling is safe.
  if (shouldRunChromiumQuotaProbeForChromeFamily() || isBrave) {
    out.opfsFlushPrivate = await detectChromeOpfsFlushPrivate();
  }
  out.timerPrecision = measureTimerPrecision();

  return out;
}

function detectFirefoxPrivateMode(storage: StorageInfo): { score: number; signals: string[] } {
  if (!isBrowser) return { score: 0, signals: [] };

  const ua = navigator.userAgent || "";
  const isFirefox = ua.includes("Firefox") && !ua.includes("Seamonkey");
  if (!isFirefox) return { score: 0, signals: [] };

  const signals: string[] = [];
  let score = 0;

  if (typeof navigator.serviceWorker === "undefined") {
    score += 0.3;
    signals.push("Service worker API blocked");
  }

  if (storage?.quotaMB && storage.quotaMB < 50) {
    score += 0.4;
    signals.push("Very low storage quota (<50MB)");
  }

  if (!window.indexedDB) {
    score += 0.5;
    signals.push("IndexedDB completely blocked");
  }

  try {
    if (!crypto.subtle) {
      score += 0.2;
      signals.push("Web Crypto API unavailable");
    }
  } catch {
    score += 0.1;
    signals.push("Crypto API access error");
  }

  try {
    if (!(navigator as any).getBattery && "getBattery" in Navigator.prototype) {
      score += 0.1;
      signals.push("Battery API blocked");
    }
  } catch {
    /* ignore */
  }

  return { score: Math.min(0.95, score), signals };
}

/**
 * Multi-signal Chromium incognito detector (Chrome, Edge, Opera, Brave).
 * Read-only — never calls Notification.requestPermission() or any prompting API.
 *
 * Weights:
 *   STRONG = 0.7   MEDIUM = 0.35   WEAK = 0.1   (clamped to 0.95)
 *
 * Signals:
 *   1. quota < 120 MiB (historical incognito cap)                      STRONG
 *   2. quota < jsHeapSizeLimit / 3                                     STRONG
 *   3. quota < jsHeapSizeLimit * 2 (legacy detectIncognito heuristic)  MEDIUM
 *   4. Notification.permission === 'denied' on top-level HTTPS         STRONG
 *   5. storage.estimate().usageDetails missing/empty                   WEAK
 *   6. webkitRequestFileSystem fallback when quota query unavailable
 */
async function detectChromiumPrivateModeViaQuota(): Promise<{ score: number; signals: string[] }> {
  if (!isBrowser) return { score: 0, signals: [] };

  const signals: string[] = [];
  let score = 0;
  const mib = (b: number) => Math.round(b / (1024 * 1024));

  const heap: number = (window as any)?.performance?.memory?.jsHeapSizeLimit ?? 1073741824;

  const quotaBytes = await new Promise<number | null>((resolve) => {
    const wts: any = (navigator as any).webkitTemporaryStorage;
    if (wts && typeof wts.queryUsageAndQuota === "function") {
      try {
        wts.queryUsageAndQuota(
          (_: number, quota: number) => resolve(typeof quota === "number" ? quota : null),
          () => resolve(null),
        );
      } catch {
        resolve(null);
      }
    } else {
      resolve(null);
    }
  }).then(async (q) => {
    if (q !== null) return q;
    try {
      const est: any = await (navigator as any).storage?.estimate?.();
      return typeof est?.quota === "number" ? est.quota : null;
    } catch {
      return null;
    }
  });

  const LEGACY_SMALL_QUOTA_BYTES = 120 * 1024 * 1024;

  if (quotaBytes !== null) {
    if (quotaBytes < LEGACY_SMALL_QUOTA_BYTES) {
      score += 0.7;
      signals.push(`STRONG quota<120MB: ${mib(quotaBytes)}MiB`);
    } else if (quotaBytes < heap / 3) {
      score += 0.7;
      signals.push(`STRONG quota<heap/3: ${mib(quotaBytes)}MiB < ${mib(heap / 3)}MiB`);
    } else if (quotaBytes < heap * 2) {
      score += 0.35;
      signals.push(`MEDIUM quota<heap*2: ${mib(quotaBytes)}MiB < ${mib(heap * 2)}MiB`);
    } else {
      signals.push(`OK quota>=heap*2: ${mib(quotaBytes)}MiB >= ${mib(heap * 2)}MiB`);
    }
  } else {
    await new Promise<void>((resolve) => {
      const fs: any = (window as any).webkitRequestFileSystem;
      if (typeof fs !== "function") {
        signals.push("WEAK no quota and no FileSystem API");
        score += 0.1;
        resolve();
        return;
      }
      try {
        fs(
          0,
          1,
          () => {
            signals.push("FileSystem API accessible (normal mode)");
            resolve();
          },
          () => {
            signals.push("STRONG FileSystem API blocked (private mode)");
            score += 0.7;
            resolve();
          },
        );
      } catch {
        signals.push("WEAK FileSystem API threw");
        score += 0.1;
        resolve();
      }
    });
  }

  try {
    if (
      typeof Notification !== "undefined" &&
      typeof location !== "undefined" &&
      location.protocol === "https:" &&
      window === window.top &&
      Notification.permission === "denied"
    ) {
      score += 0.7;
      signals.push("STRONG Notification.permission=denied (read-only, no prompt)");
    }
  } catch {
    /* ignore */
  }

  try {
    const est: any = await (navigator as any).storage?.estimate?.();
    const ud = est?.usageDetails;
    if (ud === undefined || (typeof ud === "object" && Object.keys(ud).length === 0)) {
      score += 0.1;
      signals.push("WEAK estimate.usageDetails missing/empty");
    }
  } catch {
    /* ignore */
  }

  return { score: Math.min(0.95, score), signals };
}

/** True for desktop Chromium (Chrome / Edge / Opera), not Brave/Safari/Firefox. */
function shouldRunChromiumQuotaProbeForChromeFamily(): boolean {
  if (!isBrowser) return false;
  if ((navigator as any).brave) return false;
  const ua = navigator.userAgent || "";
  if (/\bCriOS\b/i.test(ua)) return false;
  if (ua.includes("Firefox") && !ua.includes("Seamonkey")) return false;
  const isSafari =
    ua.includes("Safari") &&
    !ua.includes("Chrome") &&
    !ua.includes("Chromium") &&
    !/\bCriOS\b/i.test(ua);
  if (isSafari) return false;
  return (
    ua.includes("Chrome") ||
    ua.includes("Chromium") ||
    /\bEdg\//.test(ua) ||
    ua.includes("OPR") ||
    /Opera|OPiOS/i.test(ua)
  );
}

/**
 * Modern Chrome-family incognito probe (detectIncognito v1.7.0).
 *
 * OPFS backing differs by mode: in a normal window the origin-private file
 * system is on disk, so SyncAccessHandle.flush() performs a real fsync (slow);
 * in incognito it lives in memory, so flush() is a no-op (sub-0.05ms). We time
 * 7 flushes in a Worker (createSyncAccessHandle is Worker-only) and take the
 * 4th-smallest to shrug off scheduler jitter.
 *
 * Returns true (incognito) / false (normal) / undefined (could not run — no
 * OPFS, no Worker, CSP worker-src/blob blocked, or timeout). The SDK runs
 * embedded on customer pages, so a blocked Worker must degrade gracefully.
 */
async function detectChromeOpfsFlushPrivate(): Promise<boolean | undefined> {
  if (!isBrowser) return undefined;
  if (typeof Worker === "undefined" || !(navigator.storage && (navigator.storage as any).getDirectory))
    return undefined;

  // Self-running IIFE — the worker executes on construction and postMessages the result.
  // Do NOT use `onmessage=async()=>{…}`: that waits for a trigger message the main thread
  // never sends, so the worker never runs and the probe always times out (the 3.4.0 bug).
  const src =
    "(async()=>{try{" +
    "const r=await navigator.storage.getDirectory()," +
    "f=await(await r.getFileHandle('_fp_flush',{create:true})).createSyncAccessHandle()," +
    "b=new Uint8Array(64),t=[];" +
    "for(let i=0;i<7;i++){f.write(b,{at:0});const s=performance.now();f.flush();t.push(performance.now()-s)}" +
    "f.close();try{await r.removeEntry('_fp_flush')}catch(e){}" +
    "postMessage(t.sort((a,b)=>a-b)[3]<0.05)}catch(e){postMessage(null)}})()";

  let url: string | undefined;
  let worker: Worker | undefined;
  try {
    url = URL.createObjectURL(new Blob([src], { type: "text/javascript" }));
    worker = new Worker(url);
    const w = worker;
    return await new Promise<boolean | undefined>((resolve) => {
      const to = setTimeout(() => resolve(undefined), 1500);
      w.onmessage = (e: MessageEvent) => {
        clearTimeout(to);
        resolve(typeof e.data === "boolean" ? e.data : undefined);
      };
      w.onerror = () => {
        clearTimeout(to);
        resolve(undefined);
      };
    });
  } catch {
    return undefined; // CSP worker-src/blob: blocked, or Worker construction failed
  } finally {
    try {
      worker?.terminate();
    } catch {
      /* ignore */
    }
    if (url) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }
  }
}

function measureTimerPrecision(): number {
  if (!isBrowser) return 0;

  try {
    const _start = performance.now();
    void _start;
    const samples: number[] = [];

    for (let i = 0; i < 10; i++) {
      samples.push(performance.now());
    }

    let minPrecision = 1;
    for (const sample of samples) {
      const decimal = sample % 1;
      if (decimal === 0) {
        minPrecision = 0;
        break;
      }
    }

    return minPrecision;
  } catch {
    return 0;
  }
}
